import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  FileText,
  Users,
  DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const Compliance = () => {
  const navigate = useNavigate();

  const { data: complianceMetrics } = useQuery({
    queryKey: ["compliance-metrics"],
    queryFn: async () => {
      const [dealsRes, athletesRes] = await Promise.all([
        supabase.from("deals" as any).select("*"),
        supabase.from("athletes" as any).select("*")
      ]);

      const deals = dealsRes.data as any[] || [];
      const athletes = athletesRes.data as any[] || [];

      const totalDeals = deals.length;
      const pendingReview = deals.filter(d => d.status === 'pending_compliance').length;
      const approved = deals.filter(d => d.status === 'active').length;
      const rejected = deals.filter(d => d.status === 'rejected').length;
      
      const highRisk = deals.filter(d => d.risk_level === 'high').length;
      const mediumRisk = deals.filter(d => d.risk_level === 'medium').length;
      const lowRisk = deals.filter(d => d.risk_level === 'low').length;

      const complianceRate = totalDeals > 0 ? (approved / totalDeals) * 100 : 100;
      const avgReviewTime = 24; // Mock - would calculate from actual timestamps

      return {
        totalDeals,
        pendingReview,
        approved,
        rejected,
        highRisk,
        mediumRisk,
        lowRisk,
        complianceRate,
        avgReviewTime,
        activeAthletes: athletes.filter(a => a.eligibility_status === 'eligible').length,
        totalAthletes: athletes.length
      };
    }
  });

  const { data: recentActivity } = useQuery({
    queryKey: ["recent-compliance-activity"],
    queryFn: async () => {
      const { data } = await supabase
        .from("deals" as any)
        .select(`
          *,
          athletes (name, sport)
        `)
        .order("updated_at", { ascending: false })
        .limit(10);

      return data as any[] || [];
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const variants: any = {
      pending_compliance: { variant: "outline", icon: Clock, color: "text-yellow-600" },
      active: { variant: "default", icon: CheckCircle, color: "text-green-600" },
      rejected: { variant: "destructive", icon: AlertTriangle, color: "text-red-600" },
    };
    
    const config = variants[status] || variants.pending_compliance;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace(/_/g, " ")}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Compliance Overview</h1>
            <p className="text-muted-foreground mt-2">
              Monitor NIL compliance status and risk metrics
            </p>
          </div>
          <Button onClick={() => navigate("/dashboard/deals/pending")}>
            <Shield className="mr-2 h-4 w-4" />
            Review Queue
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
              <Shield className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {complianceMetrics?.complianceRate.toFixed(1)}%
              </div>
              <Progress value={complianceMetrics?.complianceRate || 0} className="mt-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {complianceMetrics?.approved} approved of {complianceMetrics?.totalDeals} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceMetrics?.pendingReview || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">
                Avg review time: {complianceMetrics?.avgReviewTime}h
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">High Risk Deals</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceMetrics?.highRisk || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">
                {complianceMetrics?.mediumRisk} medium, {complianceMetrics?.lowRisk} low
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Athletes</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceMetrics?.activeAthletes || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">
                of {complianceMetrics?.totalAthletes} total athletes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="activity" className="space-y-4">
          <TabsList>
            <TabsTrigger value="activity">Recent Activity</TabsTrigger>
            <TabsTrigger value="risks">Risk Analysis</TabsTrigger>
            <TabsTrigger value="actions">Quick Actions</TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Compliance Activity</CardTitle>
                <CardDescription>Latest deal updates and reviews</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivity?.map((deal) => (
                    <div
                      key={deal.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{deal.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {deal.athletes?.name} - {deal.athletes?.sport}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-bold">{formatCurrency(deal.amount)}</span>
                        {getStatusBadge(deal.status)}
                      </div>
                    </div>
                  ))}
                  {!recentActivity?.length && (
                    <p className="text-center text-muted-foreground py-8">
                      No recent activity
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="risks" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    High Risk
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{complianceMetrics?.highRisk || 0}</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Requires immediate attention
                  </p>
                  <Button 
                    variant="destructive" 
                    className="w-full mt-4"
                    onClick={() => navigate("/dashboard/deals/pending")}
                  >
                    Review Now
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-yellow-200">
                <CardHeader>
                  <CardTitle className="text-yellow-600 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Medium Risk
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{complianceMetrics?.mediumRisk || 0}</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Monitor closely
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => navigate("/dashboard/deals/pending")}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-green-200">
                <CardHeader>
                  <CardTitle className="text-green-600 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Low Risk
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{complianceMetrics?.lowRisk || 0}</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Standard monitoring
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => navigate("/dashboard/deals")}
                  >
                    View All
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/dashboard/deals/pending")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Review Pending Deals
                  </CardTitle>
                  <CardDescription>
                    {complianceMetrics?.pendingReview || 0} deals awaiting review
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/dashboard/reports")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Generate Reports
                  </CardTitle>
                  <CardDescription>
                    Export compliance and analytics data
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/dashboard/entities")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Manage Entities
                  </CardTitle>
                  <CardDescription>
                    Review associated entities and conflicts
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/dashboard/sponsors")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Institutional Sponsors
                  </CardTitle>
                  <CardDescription>
                    Monitor exclusivity and restrictions
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Compliance;
